export type Product = {
  id: string
  name: string
  slug: string
  tagline: string
  category:
    | 'Healing'
    | 'GLP-1'
    | 'Growth hormone'
    | 'Cosmetic'
    | 'Nootropic'
    | 'Anti-aging'
  goals: Array<
    | 'Recovery'
    | 'Weight management'
    | 'Performance'
    | 'Skin & Hair'
    | 'Longevity'
    | 'Sleep'
  >
  purity: number
  doseMg: number
  volumeMl: number
  price: number
  batch: string
  image: string
  badge?: 'Bestseller' | 'Low stock' | 'New'
  inStock: boolean
}

// Deterministic per-product rating + review count, derived from product attributes.
// Bestsellers get higher counts; ratings cluster 4.6 – 4.9 to feel real.
export function getProductReview(p: Pick<Product, 'slug' | 'badge'>) {
  const seed = p.slug.split('').reduce((s, c) => s + c.charCodeAt(0), 0)
  const base = p.badge === 'Bestseller' ? 4.85 : p.badge === 'New' ? 4.7 : 4.78
  const variance = ((seed % 10) - 5) * 0.02
  const rating = Math.max(4.5, Math.min(4.95, base + variance))
  const baseCount =
    p.badge === 'Bestseller' ? 240 : p.badge === 'New' ? 32 : 118
  const reviewCount = baseCount + (seed % 70)
  return { rating: Number(rating.toFixed(1)), reviewCount }
}

export const PRODUCTS: Product[] = [
  {
    id: 'bpc-157',
    name: 'BPC-157',
    slug: 'bpc-157',
    tagline: 'Body protective compound for tissue repair.',
    category: 'Healing',
    goals: ['Recovery'],
    purity: 99.7,
    doseMg: 5,
    volumeMl: 10,
    price: 44,
    batch: 'BP-24091',
    image:
      'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?auto=format&fit=crop&w=900&q=80',
    badge: 'Bestseller',
    inStock: true,
  },
  {
    id: 'tb-500',
    name: 'TB-500',
    slug: 'tb-500',
    tagline: 'Thymosin Beta-4 fragment for soft-tissue research.',
    category: 'Healing',
    goals: ['Recovery', 'Performance'],
    purity: 99.4,
    doseMg: 5,
    volumeMl: 10,
    price: 59,
    batch: 'TB-24088',
    image:
      'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
  {
    id: 'semaglutide',
    name: 'Semaglutide',
    slug: 'semaglutide',
    tagline: 'GLP-1 receptor agonist research peptide.',
    category: 'GLP-1',
    goals: ['Weight management'],
    purity: 99.8,
    doseMg: 5,
    volumeMl: 10,
    price: 129,
    batch: 'SM-24102',
    image:
      'https://images.unsplash.com/photo-1576602976047-174e57a47881?auto=format&fit=crop&w=900&q=80',
    badge: 'Bestseller',
    inStock: true,
  },
  {
    id: 'tirzepatide',
    name: 'Tirzepatide',
    slug: 'tirzepatide',
    tagline: 'Dual GIP/GLP-1 receptor research compound.',
    category: 'GLP-1',
    goals: ['Weight management'],
    purity: 99.6,
    doseMg: 10,
    volumeMl: 10,
    price: 179,
    batch: 'TZ-24104',
    image:
      'https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=900&q=80',
    badge: 'Low stock',
    inStock: true,
  },
  {
    id: 'ghk-cu',
    name: 'GHK-Cu',
    slug: 'ghk-cu',
    tagline: 'Copper tripeptide for skin and collagen research.',
    category: 'Cosmetic',
    goals: ['Skin & Hair', 'Longevity'],
    purity: 99.5,
    doseMg: 50,
    volumeMl: 10,
    price: 69,
    batch: 'GH-24076',
    image:
      'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
  {
    id: 'nad-plus',
    name: 'NAD+',
    slug: 'nad-plus',
    tagline: 'Nicotinamide adenine dinucleotide.',
    category: 'Anti-aging',
    goals: ['Longevity', 'Performance'],
    purity: 99.2,
    doseMg: 500,
    volumeMl: 10,
    price: 99,
    batch: 'ND-24070',
    image:
      'https://images.unsplash.com/photo-1606206522398-de2cf2c4fe0a?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
  {
    id: 'epitalon',
    name: 'Epitalon',
    slug: 'epitalon',
    tagline: 'Pineal-derived tetrapeptide for longevity research.',
    category: 'Anti-aging',
    goals: ['Longevity', 'Sleep'],
    purity: 99.6,
    doseMg: 10,
    volumeMl: 10,
    price: 54,
    batch: 'EP-24061',
    image:
      'https://images.unsplash.com/photo-1582719471384-894fbb16e074?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
  {
    id: 'cjc-1295',
    name: 'CJC-1295',
    slug: 'cjc-1295',
    tagline: 'GHRH analogue for growth hormone research.',
    category: 'Growth hormone',
    goals: ['Performance', 'Recovery'],
    purity: 99.3,
    doseMg: 5,
    volumeMl: 10,
    price: 64,
    batch: 'CJ-24082',
    image:
      'https://images.unsplash.com/photo-1626716493137-b67fe9501e76?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
  {
    id: 'ipamorelin',
    name: 'Ipamorelin',
    slug: 'ipamorelin',
    tagline: 'Selective growth hormone secretagogue.',
    category: 'Growth hormone',
    goals: ['Performance', 'Recovery', 'Sleep'],
    purity: 99.5,
    doseMg: 5,
    volumeMl: 10,
    price: 54,
    batch: 'IP-24079',
    image:
      'https://images.unsplash.com/photo-1612277795421-9bc7706a4a34?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
  {
    id: 'tesamorelin',
    name: 'Tesamorelin',
    slug: 'tesamorelin',
    tagline: 'GHRH analogue for metabolic research.',
    category: 'Growth hormone',
    goals: ['Weight management', 'Performance'],
    purity: 99.4,
    doseMg: 5,
    volumeMl: 10,
    price: 89,
    batch: 'TS-24067',
    image:
      'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
  {
    id: 'mots-c',
    name: 'MOTS-c',
    slug: 'mots-c',
    tagline: 'Mitochondrial-derived metabolic peptide.',
    category: 'Anti-aging',
    goals: ['Longevity', 'Performance', 'Weight management'],
    purity: 99.1,
    doseMg: 10,
    volumeMl: 10,
    price: 109,
    batch: 'MT-24055',
    image:
      'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=900&q=80',
    badge: 'New',
    inStock: true,
  },
  {
    id: 'selank',
    name: 'Selank',
    slug: 'selank',
    tagline: 'Synthetic heptapeptide for cognitive research.',
    category: 'Nootropic',
    goals: ['Sleep'],
    purity: 99.0,
    doseMg: 5,
    volumeMl: 10,
    price: 49,
    batch: 'SL-24050',
    image:
      'https://images.unsplash.com/photo-1559757175-7b21671636f0?auto=format&fit=crop&w=900&q=80',
    inStock: true,
  },
]
